import React, { useState } from 'react';
import { X, Globe, Clock, Timer } from 'lucide-react';
import LoadingSpinner from '../UI/LoadingSpinner';

const MonitorModal = ({ onClose, onSave, initialData }) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    url: initialData?.url || '',
    interval: initialData?.interval || 300000, // 5 minutes default
    timeout: initialData?.timeout || 10000 // 10 seconds default
  });
  const [loading, setLoading] = useState(false);

  const intervalOptions = [
    { value: 60000, label: '1 minute' },
    { value: 300000, label: '5 minutes' },
    { value: 600000, label: '10 minutes' },
    { value: 1800000, label: '30 minutes' },
    { value: 3600000, label: '1 hour' }
  ];

  const timeoutOptions = [
    { value: 5000, label: '5 seconds' },
    { value: 10000, label: '10 seconds' },
    { value: 15000, label: '15 seconds' },
    { value: 30000, label: '30 seconds' },
    { value: 60000, label: '1 minute' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'interval' || name === 'timeout' ? parseInt(value) : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onSave(formData);
    } catch (error) {
      console.error('Save monitor error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUrlChange = (e) => {
    if (initialData) return; // Don't allow URL change when editing

    let url = e.target.value;

    // Auto-add https:// if no protocol is specified
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    setFormData({
      ...formData,
      url,
      // Auto-generate name from URL if name is empty
      name: formData.name || url.replace(/^https?:\/\//, '').replace(/\/$/, '')
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in backdrop-blur-sm">
      <div className="bg-white dark:bg-surface-900 rounded-[2rem] shadow-premium-xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-surface-200 dark:border-surface-800 animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-8 border-b border-surface-100 dark:border-surface-800/50">
          <div>
            <h2 className="text-2xl font-bold text-surface-900 dark:text-white">
              {initialData ? 'Update Monitor' : 'Deploy Monitor'}
            </h2>
            <p className="text-sm text-surface-500 font-medium">
              {initialData ? 'Refine your monitoring node configuration' : 'Configure a new global monitoring target'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-surface-400 hover:text-surface-900 dark:hover:text-white transition-colors rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          {/* Name */}
          <div className="space-y-3">
            <label htmlFor="name" className="block text-sm font-bold text-surface-700 dark:text-surface-300 uppercase tracking-widest">
              Monitor Display Name
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Globe className="h-5 w-5 text-surface-400 group-focus-within:text-brand-500 transition-colors" />
              </div>
              <input
                type="text"
                id="name"
                name="name"
                required
                className="input-premium pl-12"
                placeholder="Production API Cluster"
                value={formData.name}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* URL */}
          <div className="space-y-3">
            <label htmlFor="url" className="block text-sm font-bold text-surface-700 dark:text-surface-300 uppercase tracking-widest">
              Website URL / Endpoint
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Globe className="h-5 w-5 text-surface-400 group-focus-within:text-brand-500 transition-colors" />
              </div>
              <input
                type="url"
                id="url"
                name="url"
                required
                disabled={!!initialData}
                className={`input-premium pl-12 ${initialData ? 'opacity-50 cursor-not-allowed bg-surface-50 dark:bg-surface-950' : ''}`}
                placeholder="https://api.example.com/v1"
                value={formData.url}
                onChange={handleUrlChange}
              />
            </div>
            {!initialData && (
              <p className="text-xs text-surface-500 font-medium italic">
                Protocol (http/https) will be added automatically if omitted.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Check Interval */}
            <div className="space-y-3">
              <label htmlFor="interval" className="block text-sm font-bold text-surface-700 dark:text-surface-300 uppercase tracking-widest">
                Frequency
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Clock className="h-5 w-5 text-surface-400" />
                </div>
                <select
                  id="interval"
                  name="interval"
                  className="input-premium pl-12 appearance-none"
                  value={formData.interval}
                  onChange={handleChange}
                >
                  {intervalOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Timeout */}
            <div className="space-y-3">
              <label htmlFor="timeout" className="block text-sm font-bold text-surface-700 dark:text-surface-300 uppercase tracking-widest">
                Timeout
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Timer className="h-5 w-5 text-surface-400" />
                </div>
                <select
                  id="timeout"
                  name="timeout"
                  className="input-premium pl-12 appearance-none"
                  value={formData.timeout}
                  onChange={handleChange}
                >
                  {timeoutOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary !py-4"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 btn-primary !py-4 shadow-glow-brand"
              disabled={loading}
            >
              {loading ? (
                <LoadingSpinner size="sm" />
              ) : (
                initialData ? 'Save Changes' : 'Deploy Monitor'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MonitorModal;
